import React, { useState, useEffect } from 'react';
import { getStudents, saveStudent, deleteStudent, getUniversities } from '../services/apiService';
import { supabase } from '../services/supabase';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  X, 
  User, 
  GraduationCap, 
  Loader2, 
  Upload,
  MapPin,
  Building2,
  FileText
} from 'lucide-react';

const AdminStudents = () => {
  const [students, setStudents] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const { toast, showToast, hideToast } = useToast();
  const [editingStudent, setEditingStudent] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: '',
    university_id: '',
    university_name: '',
    country: '',
    visa_type: '',
    visa_document_url: '',
    story: '',
    status: 'placed',
    intake_year: new Date().getFullYear(),
    intake_month: '',
  });

  useEffect(() => {
    loadStudents();
    loadUniversities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStudents = async () => {
    try {
      setIsLoading(true);
      const data = await getStudents();
      setStudents(data);
    } catch (error) {
      console.error('Error loading students:', error);
      showToast('Failed to load students', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const loadUniversities = async () => {
    try {
      const data = await getUniversities();
      setUniversities(data);
    } catch (error) {
      console.error('Error loading universities:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // If university_id changes, update university_name
    if (name === 'university_id') {
      const selectedUni = universities.find(u => u.id === parseInt(value));
      setFormData(prev => ({
        ...prev,
        university_id: value,
        university_name: selectedUni ? selectedUni.name : prev.university_name,
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, photo: file });
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      let photoUrl = editingStudent?.photo_url;

      if (formData.photo) {
        const fileExt = formData.photo.name.split('.').pop();
        const fileName = `student_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `students/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('photos')
          .upload(filePath, formData.photo);

        if (uploadError) {
          console.error('Photo upload error:', uploadError);
          showToast('Failed to upload photo. Please try again.', 'error');
          setSaving(false);
          return;
        }

        const { data: { publicUrl } } = supabase.storage.from('photos').getPublicUrl(filePath);
        photoUrl = publicUrl;
      }

      let visaDocumentUrl = editingStudent?.visa_document_url;
      if (formData.visa_document_file) {
        const fileExt = formData.visa_document_file.name.split('.').pop();
        const fileName = `visa_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `students/visa/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('photos')
          .upload(filePath, formData.visa_document_file);

        if (uploadError) {
          console.error('Visa document upload error:', uploadError);
          showToast('Failed to upload visa document. Please try again.', 'error');
          setSaving(false);
          return;
        }

        const { data: { publicUrl } } = supabase.storage.from('photos').getPublicUrl(filePath);
        visaDocumentUrl = publicUrl;
      }

      const { photo, visa_document_file, ...dataToSave } = formData;
      await saveStudent({
        ...dataToSave,
        photo_url: photoUrl,
        visa_document_url: visaDocumentUrl,
        university_id: formData.university_id ? parseInt(formData.university_id) : null,
        intake_year: formData.intake_year ? parseInt(formData.intake_year) : null,
        id: editingStudent?.id,
      });
      
      handleCloseForm();
      loadStudents();
      showToast(editingStudent ? 'Student updated successfully!' : 'Student added successfully!', 'success');
    } catch (error) {
      console.error('Error saving student:', error);
      showToast('Error saving student. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setFormData({
      name: student.name || '',
      email: student.email || '',
      phone: student.phone || '',
      course: student.course || '',
      university_id: student.university_id || '',
      university_name: student.university_name || '',
      country: student.country || '',
      visa_type: student.visa_type || '',
      visa_document_url: student.visa_document_url || '',
      story: student.story || '',
      status: student.status || 'placed',
      intake_year: student.intake_year || new Date().getFullYear(),
      intake_month: student.intake_month || '',
      photo: null,
    });
    setPreviewUrl(student.photo_url || null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        await deleteStudent(id);
        loadStudents();
        showToast('Student deleted successfully!', 'success');
        // Dispatch event to refresh dashboard
        window.dispatchEvent(new CustomEvent('dataUpdated'));
      } catch (error) {
        console.error('Error deleting student:', error);
        showToast('Error deleting student. Please try again.', 'error');
      }
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingStudent(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      course: '',
      university_id: '',
      university_name: '',
      country: '',
      visa_type: '',
      visa_document_url: '',
      story: '',
      status: 'placed',
      intake_year: new Date().getFullYear(),
      intake_month: '',
      photo: null,
    });
    setPreviewUrl(null);
  };

  return (
    <div className="space-y-8">
      <Toast toast={toast} hideToast={hideToast} />
      
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Students Management</h1>
          <p className="text-slate-500 text-sm mt-1">Manage students placed in universities.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20"
        >
          <Plus size={18} />
          Add Student
        </button>
      </div>

      {/* STUDENTS GRID */}
      {isLoading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-red-600 mb-4"></div>
          <p className="text-slate-500">Loading students...</p>
        </div>
      ) : students.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {students.map((student) => (
            <div key={student.id} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all flex flex-col h-full">
              
              {/* Student Header */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center shrink-0 border border-slate-100">
                  {(() => {
                    const url = student.photo_url;
                    const safeUrl = url && !url.startsWith('blob:') ? url : null;
                    return safeUrl ? (
                      <img
                        src={safeUrl}
                        alt={student.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null;
                  })()}
                  {(() => {
                    const url = student.photo_url;
                    const hasValidPhoto = url && !url.startsWith('blob:');
                    return !hasValidPhoto ? (
                      <User size={24} className="text-slate-400" />
                    ) : null;
                  })()}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-slate-900 leading-tight truncate">{student.name}</h3>
                  <p className="text-sm text-slate-600 truncate">{student.course}</p>
                </div>
              </div>

              {/* Student Details */}
              <div className="space-y-2 mb-4 flex-1">
                {student.university_name && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Building2 size={14} className="text-slate-400" />
                    <span className="truncate">{student.university_name}</span>
                  </div>
                )}
                {student.country && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <MapPin size={14} className="text-slate-400" />
                    <span>{student.country}</span>
                  </div>
                )}
                {student.visa_type && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <FileText size={14} className="text-slate-400" />
                    <span>{student.visa_type}</span>
                  </div>
                )}
                <div className="mt-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    student.status === 'graduated' ? 'bg-green-100 text-green-700' :
                    student.status === 'enrolled' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {student.status}
                  </span>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="pt-4 border-t border-slate-50 flex justify-between items-center mt-auto">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  {student.intake_year && student.intake_month 
                    ? `${student.intake_month} ${student.intake_year}`
                    : student.intake_year || 'N/A'}
                </span>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(student)}
                    className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="Edit"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(student.id)}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
            <GraduationCap size={32} />
          </div>
          <h3 className="text-lg font-bold text-slate-900">No students yet</h3>
          <p className="text-slate-500 mt-1">Add a student to track placements.</p>
        </div>
      )}

      {/* FORM MODAL */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
            
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 shrink-0">
              <h2 className="text-lg font-bold text-slate-900">
                {editingStudent ? 'Edit Student' : 'New Student'}
              </h2>
              <button onClick={handleCloseForm} className="text-slate-400 hover:text-red-600 transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* Modal Body (Scrollable) */}
            <div className="p-6 overflow-y-auto">
              <form onSubmit={handleSubmit} className="space-y-5">
                
                {/* Photo Upload */}
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                    {previewUrl ? (
                      <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <User size={32} className="text-slate-300" />
                    )}
                  </div>
                  <label className="flex-1">
                    <div className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                      <Upload size={18} className="text-slate-400" />
                      <span className="text-sm font-medium text-slate-700">Upload Photo</span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                  />
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                    />
                  </div>
                </div>

                {/* Course */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Course *</label>
                  <input
                    type="text"
                    name="course"
                    required
                    value={formData.course}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                  />
                </div>

                {/* University */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">University</label>
                  <select
                    name="university_id"
                    value={formData.university_id}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                  >
                    <option value="">Select University</option>
                    {universities.map(uni => (
                      <option key={uni.id} value={uni.id}>{uni.name} - {uni.country}</option>
                    ))}
                  </select>
                  {!formData.university_id && (
                    <input
                      type="text"
                      name="university_name"
                      value={formData.university_name}
                      onChange={handleChange}
                      placeholder="Or enter university name manually"
                      className="w-full mt-2 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                    />
                  )}
                </div>

                {/* Country & Visa Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Country</label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Visa Type</label>
                    <input
                      type="text"
                      name="visa_type"
                      value={formData.visa_type}
                      onChange={handleChange}
                      placeholder="e.g., UK Student Visa"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                    />
                  </div>
                </div>

                {/* Visa Document Upload */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Visa Document</label>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setFormData({ ...formData, visa_document_file: file });
                      }
                    }}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                  />
                  {formData.visa_document_url && (
                    <p className="text-xs text-slate-500 mt-1">Current: {formData.visa_document_url}</p>
                  )}
                </div>

                {/* Status & Intake */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Status</label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                    >
                      <option value="placed">Placed</option>
                      <option value="enrolled">Enrolled</option>
                      <option value="graduated">Graduated</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Intake Year</label>
                    <input
                      type="number"
                      name="intake_year"
                      value={formData.intake_year}
                      onChange={handleChange}
                      min="2020"
                      max="2030"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                    />
                  </div>
                </div>

                {/* Intake Month */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Intake Month</label>
                  <select
                    name="intake_month"
                    value={formData.intake_month}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                  >
                    <option value="">Select Month</option>
                    {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(month => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                </div>

                {/* Story */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">Story</label>
                  <textarea
                    name="story"
                    rows={4}
                    value={formData.story}
                    onChange={handleChange}
                    placeholder="Student success story..."
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-red-600"
                  />
                </div>

                {/* Submit Button */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-red-600 text-white font-bold py-3 rounded-lg hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {saving ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      editingStudent ? 'Update Student' : 'Add Student'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="px-6 py-3 border border-slate-300 text-slate-700 font-bold rounded-lg hover:bg-slate-50 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStudents;

